import React, { useState, useEffect } from 'react';

import { useClient } from 'shared/apollo';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { useUser } from 'shared/auth/context';
import { setAuthorizationHeader } from 'donor/utils';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import { TableRoot, TableRow, Heading, RowH, ColH, LinkClear, CloseIcon, BoxCenter } from './styles';
import { disableUser, unlockUser, verifyUser } from './api';
import UserModal from './UserModal';
import PasswordInstructions, {
  getPasswordValidations,
} from 'shared/no-auth/components/AccountEditor/PasswordInstructions';
import { generatePassword } from 'shared/utils/passwordUtils';

type UserType = { 
  dbId: number; 
  firstName: string; 
  lastName: string; 
  email: string; 
  title: string; 
  phone: string; 
  enabled: boolean; 
  userType: string; 
  pronoun?: string;
  isLocked?: boolean;
  isVerified?: boolean;
}

type UsersType = UserType[];

const Row: React.FC = ({
  row,
  pending,
  onPressDelete,
  onPressEdit,
  onPressUnlock,
  onPressVerify,
  onPressGhosting,
  onPressResetPassword,
}) => (
  <TableRow>
    <TableCell align="left">{row.dbId}</TableCell>
    <TableCell align="left">{row.userType}</TableCell>
    <TableCell align="left">{row.email}</TableCell>
    <TableCell align="left">{row.firstName}</TableCell>
    <TableCell align="left">{row.lastName}</TableCell>
    <TableCell align="left">{row?.pronoun||'-'}</TableCell>
    <TableCell align="left">{row?.phone||'-'}</TableCell>
    <TableCell align="left">{row?.title||'-'}</TableCell>
    <TableCell align="left">
      <span>{row?.isLocked ? 'Yes ' : 'No '}</span>
      {pending === 'unlock'
        ? <CircularProgress size={14} style={{marginLeft: 8}} />
        : row?.isLocked&&(<LinkClear onClick={() => onPressUnlock(row)}>Unlock</LinkClear>)
      }
    </TableCell>
    <TableCell align="left">
      {row?.isVerified ? 'Yes ' : 'No '}
      {!row?.isVerified&&(pending === 'verify'
        ? <CircularProgress size={14} style={{marginLeft: 8}} />
        : <LinkClear onClick={() => onPressVerify(row)}>Verify</LinkClear>
      )}
    </TableCell>
    <TableCell align="center">
      <IconButton
        size="small"
        title="Reset password and email it to the user"
        onClick={() => onPressResetPassword(row)}
      >
        <EmailIcon />
      </IconButton>
    </TableCell>
    <TableCell align="center" onClick={() => onPressGhosting(row)}><PersonIcon /></TableCell>
    <TableCell align="center" onClick={() => onPressEdit(row)}><EditIcon /></TableCell>
    <TableCell align="center" onClick={() => onPressDelete(row)}><DeleteIcon /></TableCell>
  </TableRow>
);

const UsersModal: React.FC = ({ open, setOpen, handleClose, account }) => {
    const client = useClient();
    const effectiveUser = useUser();
    const [search, setSearch] = useState<string>('');
    const [allUsers, setAllUsers] = useState<UsersType>([]);
    const [users, setUsers] = useState<UsersType>([]);
    const [selectedUser, setSelectedUser] = useState<UserType>(undefined);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false);
    const [userModalOpen, setUserModalOpen] = useState<boolean>(false);
    const [resetModalOpen, setResetModalOpen] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [repassword, setRepassword] = useState<string>('');
    const [passwordValidations, setPasswordValidations] = useState<boolean[]>([]);
    const [actionError, setActionError] = useState<string>('');
    const [actionMessage, setActionMessage] = useState<string>('');
    const [pendingAction, setPendingAction] = useState<{dbId: number; action: string} | null>(null);
    const [resetLoading, setResetLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    useEffect(() => {
      setAllUsers(account?.users??[]);
      setUsers(account?.users??[]);
    }, []);

    const handleSearchUsers = (searchString: string) => {
      setSearch(searchString);
      const lowerCaseSearch = searchString.toLowerCase();
      const filteredUsers = (allUsers).filter(user => {
        return user.dbId.toString().includes(lowerCaseSearch) ||
          user.firstName.toLowerCase().includes(lowerCaseSearch) ||
          user.lastName.toLowerCase().includes(lowerCaseSearch) ||
          user.email.toLowerCase().includes(lowerCaseSearch) ||
          user.title.toLowerCase().includes(lowerCaseSearch) ||
          user.userType.toLowerCase().includes(lowerCaseSearch);
      });
      setUsers(filteredUsers);
    };

    const handleDelete =  (user: UserType) => {
      setSelectedUser(user);
      setDeleteModalOpen(true);
    };

    const handleEdit =  (user: UserType) => {
      setSelectedUser(user);
      setUserModalOpen(true);
    };

    const applyUserUpdate = (dbId: number, changes: Partial<UserType>) => {
      const merge = (list: UsersType) => list.map(user =>
        user.dbId === dbId ? {...user, ...changes} : user
      );
      setAllUsers(prev => merge(prev));
      setUsers(prev => merge(prev));
      setHasMadeChanges(true);
    };

    const handleUnlock = async (userToUnlock: UserType) => {
      setActionError('');
      setActionMessage('');
      setPendingAction({dbId: userToUnlock.dbId, action: 'unlock'});
      try {
        const result = await unlockUser(client, {id: userToUnlock.dbId});
        if (!result.success) {
          setActionError(result.error || 'Unable to unlock the user');
          return;
        }
        applyUserUpdate(userToUnlock.dbId, {isLocked: false});
        setSelectedUser(undefined);
        setActionMessage(`${userToUnlock.email} unlocked.`);
      } finally {
        setPendingAction(null);
      }
    };

    const handleVerify = async (userToVerify: UserType) => {
      setActionError('');
      setActionMessage('');
      setPendingAction({dbId: userToVerify.dbId, action: 'verify'});
      try {
        const result = await verifyUser(client, {id: userToVerify.dbId});
        if (!result.success) {
          setActionError(result.error || 'Unable to verify the user');
          return;
        }
        applyUserUpdate(userToVerify.dbId, {isVerified: true});
        setSelectedUser(undefined);
        setActionMessage(`${userToVerify.email} verified.`);
      } finally {
        setPendingAction(null);
      }
    };

    const handleOpenResetPassword = (userToReset: UserType) => {
      setActionError('');
      setActionMessage('');
      setPassword('');
      setRepassword('');
      setPasswordValidations([]);
      setShowPassword(false);
      setSelectedUser(userToReset);
      setResetModalOpen(true);
    };

    const handleGeneratePassword = () => {
      const generated = generatePassword();
      setPassword(generated);
      setRepassword(generated);
      setPasswordValidations(getPasswordValidations(generated));
      setShowPassword(true);
      setActionError('');
    };

    const handleRunResetPassword = async () => {
      setActionError('');
      setActionMessage('');
      if (!passwordValidations.every(item => item === true)) {
        setActionError('Password is not valid');
        return;
      }
      if (password !== repassword) {
        setActionError('Passwords do not match');
        return;
      }
      setResetLoading(true);
      try {
        const result = await unlockUser(client, {id: selectedUser.dbId, password});
        if (!result.success) {
          setActionError(result.error || 'Unable to reset the password');
          return;
        }
        applyUserUpdate(selectedUser.dbId, {isLocked: false});
        setResetModalOpen(false);
        setActionMessage(`Password reset and emailed to ${selectedUser.email}.`);
        setSelectedUser(undefined);
      } finally {
        setResetLoading(false);
      }
    };

    const handleGhosting = async (userToGhost: UserType) => {
      await effectiveUser.signInAs(userToGhost.email);
      await setAuthorizationHeader();
      if (account?.type === 'grantee') {
        window.location.href = '/npo/todo/by-due-date';
      } else {
        window.location.href = '/';
      }
    };

    const handleRunDisable = async () => {
      const updatedList = allUsers.filter(user => user.dbId !== selectedUser.dbId);
      setAllUsers(updatedList);
      setUsers(updatedList);
      setSearch('');
      setSelectedUser(undefined);
      setDeleteModalOpen(false);
      setHasMadeChanges(true);
      await disableUser(client, {id: selectedUser.dbId});
    };

    const handleDone = () => {
      handleClose({success: hasMadeChanges});
    };

    const handleCloseSingleUserModal = (newUser) => {
      if (!newUser || !newUser?.firstName) {
        setSelectedUser(undefined);
        setUserModalOpen(false);
       return; 
      }
      const isNew = !newUser.id;
      let updatedList = [];

      if (isNew) {
        updatedList = [...allUsers];
        updatedList.push({...newUser, isVerified: true, dbId: newUser?.newUserId??'Saving...'});
      } else {
        updatedList = allUsers.map(user => 
          user.dbId === newUser.id ? {...user, ...newUser} : user
        );
      }
      if (newUser) {
        setAllUsers(updatedList);
        setUsers(updatedList);
        setSearch('');
        setHasMadeChanges(true);
      }
      setSelectedUser(undefined);
      setUserModalOpen(false);
    };

    return (
      <TrueImpactModal
        open={open}
        hideHeader={true}
        next={{
          text: 'Done',
          onClick: handleDone,
        }}
        onClose={handleDone}
      >
        <Heading>
          <CloseIcon onClick={handleDone} />
          <RowH>
            <ColH>
              <Typography variant="h2">Users List</Typography>
              <br/>
              <Typography variant="body1">Below is a comprehensive list of users that belong
              to <b>{account?.name||'-'} (ID: {account?.dbId})</b></Typography>
            </ColH>
          </RowH>
          <RowH>
            <ColH>
              <TextField placeholder="Search" value={search} onChange={e => handleSearchUsers(e.target.value)}
                InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon /></InputAdornment>)}}
              />
            </ColH>
             <ColH style={{marginTop: '10px'}}><span>Total matching users: {users.length}</span></ColH>
             <Button onClick={() => {
                setUserModalOpen(true);
                setSelectedUser(undefined);
             }}>Add new user</Button> 
          </RowH>
          {actionError && (
            <RowH>
              <Typography style={{ color: '#ff7272' }} variant="body2">{actionError}</Typography>
            </RowH>
          )}
          {actionMessage && (
            <RowH>
              <Typography variant="body2">{actionMessage}</Typography>
            </RowH>
          )}
        </Heading>
        <TableRoot>
          <Table aria-label="accounts table">
              <TableHead>
                  <TableRow>
                      <TableCell align="left">ID</TableCell>
                      <TableCell align="left">Type</TableCell>
                      <TableCell align="left" style={{minWidth: '210px'}}>Email</TableCell>
                      <TableCell align="left">First name</TableCell>
                      <TableCell align="left">Last name</TableCell>
                      <TableCell align="left">Pronoun</TableCell>
                      <TableCell align="left">Phone</TableCell>
                      <TableCell align="left">Job Title</TableCell>
                      <TableCell align="left" style={{minWidth: '210px'}}>User locked?</TableCell>
                      <TableCell align="left">User Verified?</TableCell>
                      <TableCell align="center" style={{minWidth: '140px'}}>Reset password</TableCell>
                      <TableCell align="left">Ghosting</TableCell>
                      <TableCell align="left">Edit</TableCell>
                      <TableCell align="left">Remove</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <Row
                    key={user.id}
                    row={user}
                    pending={pendingAction?.dbId === user.dbId ? pendingAction.action : undefined}
                    onPressDelete={handleDelete}
                    onPressEdit={handleEdit}
                    onPressUnlock={handleUnlock}
                    onPressVerify={handleVerify}
                    onPressGhosting={handleGhosting}
                    onPressResetPassword={handleOpenResetPassword}
                  />
                ))}
              </TableBody>
          </Table>
          {users.length === 0 && (
            <BoxCenter>
              <DissatisfiedIcon style={{ fontSize: 40 }}/>
              <Typography variant="body1">No users found for the current account.</Typography>
            </BoxCenter>
          )}
        </TableRoot>

        <TrueImpactModal
          title="Confirm User Deletion"
          maxWidth="500px"
          maxHeight="500px"
          open={deleteModalOpen}
          hideHeader={false}
          next={{
            text: 'Confirm',
            onClick: handleRunDisable,
          }}
          previous={{
            text: 'Cancel',
            onClick: () => setDeleteModalOpen(false),
          }}
          onClose={() => setDeleteModalOpen(false)}
        >
          <RowH style={{padding: 22, textAlign: 'center'}}>
            <Typography variant="body2">
              Are you sure you want to delete this user? Please confirm to
              proceed:<br/><b>{selectedUser?.email||'-'} (ID: {selectedUser?.dbId})</b>
            </Typography>
          </RowH>
        </TrueImpactModal>
        <TrueImpactModal
          title="Reset user password"
          maxWidth="600px"
          open={resetModalOpen}
          hideHeader={false}
          next={{
            text: resetLoading ? 'Resetting...' : 'Reset password',
            onClick: resetLoading ? () => undefined : handleRunResetPassword,
          }}
          previous={{
            text: 'Cancel',
            onClick: () => setResetModalOpen(false),
          }}
          onClose={() => setResetModalOpen(false)}
        >
          <ColH style={{padding: 22}}>
            <Typography variant="body2">
              Sets a new password for <b>{selectedUser?.email||'-'}</b> and emails it to the user.
            </Typography>
            <RowH style={{marginTop: 16}}>
              <ColH style={{marginRight: '10px'}}>
                <p>Password:</p>
                <TextField
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    setPasswordValidations(getPasswordValidations(e.target.value));
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          title={showPassword ? 'Hide passwords' : 'Show passwords'}
                          onClick={() => setShowPassword(prev => !prev)}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </ColH>
              <ColH>
                <p>Confirm Password:</p>
                <TextField
                  placeholder="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  value={repassword}
                  onChange={e => setRepassword(e.target.value)}
                />
              </ColH>
            </RowH>
            <RowH style={{marginTop: 12}}>
              <Button variant="outlined" onClick={handleGeneratePassword}>
                Generate password
              </Button>
            </RowH>
            <RowH>
              <PasswordInstructions
                passwordValidations={passwordValidations}
                password={password}
                confirmPassword={repassword}
              />
            </RowH>
            {resetLoading && (
              <RowH style={{marginTop: 8}}>
                <CircularProgress size={18} />
              </RowH>
            )}
            {actionError && (
              <Typography style={{ color: '#ff7272' }} variant="body2">{actionError}</Typography>
            )}
          </ColH>
        </TrueImpactModal>
        {userModalOpen&&(<UserModal
          open={userModalOpen}
          setOpen={setUserModalOpen}
          user={selectedUser}
          handleClose={handleCloseSingleUserModal}
          accountId={account?.dbId}
        />)}
      </TrueImpactModal>
    );
};

export default UsersModal;
